import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Register() {
  const { register, tenant, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", business_name: "", industry: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const hasPending = typeof window !== "undefined" && !!localStorage.getItem("fisbo_pending_diagnostic");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register({ ...form, tenant_slug: tenant?.slug || "fisbo" });
      // Submit the stashed diagnostic right away so the roadmap + GHL webhook
      // fire at signup on every device — not deferred to the dashboard mount.
      const pending = localStorage.getItem("fisbo_pending_diagnostic");
      if (pending) {
        try {
          await client.post("/diagnostic/submit", { answers: JSON.parse(pending) });
          localStorage.removeItem("fisbo_pending_diagnostic");
        } catch (e2) {
          // leave answers stashed; the Dashboard will retry as a safety net
          console.warn("Deferred diagnostic submit failed at signup; will retry on dashboard.", e2);
        }
      }
      localStorage.setItem("fisbo_new_signup", "1");
      toast.success(hasPending ? "Account created — unlocking your roadmap!" : "Account created!");
      navigate("/welcome");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  const field = "min-h-[48px] w-full rounded-xl border border-neutral-200 px-4 outline-none transition-all focus:border-[var(--tenant-primary)] focus:ring-2 focus:ring-[var(--tenant-primary-light)]";

  return (
    <div className="grid min-h-screen place-items-center bg-[#FAF9F6] px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><Link to="/"><Logo /></Link></div>
        <div className="rounded-2xl border border-[#E5E2DC] bg-white p-8">
          {hasPending && (
            <div className="mb-5 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium tenant-text" style={{ backgroundColor: "var(--tenant-primary-light)" }} data-testid="pending-diagnostic-banner">
              🎉 Your roadmap is ready — create a free account to unlock it.
            </div>
          )}
          <h1 className="font-head text-2xl font-semibold text-neutral-900">{hasPending ? "Almost there — save your roadmap" : "Create your free account"}</h1>
          <p className="mt-1 text-neutral-600">{hasPending ? "Create your free account to see your personalized results." : "This takes 30 seconds. No numbers required yet."}</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">Your name</label>
              <input data-testid="register-name" required value={form.name} onChange={set("name")} className={field} placeholder="Alex Rivera" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">Email</label>
              <input data-testid="register-email" type="email" required value={form.email} onChange={set("email")} className={field} placeholder="you@business.com" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">Password</label>
              <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className={field} placeholder="At least 6 characters" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-neutral-700">Business name</label>
                <input data-testid="register-business" value={form.business_name} onChange={set("business_name")} className={field} placeholder="Optional" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-neutral-700">Industry</label>
                <input data-testid="register-industry" value={form.industry} onChange={set("industry")} className={field} placeholder="Optional" />
              </div>
            </div>
            {error && <p data-testid="register-error" className="text-sm text-[#D96C4A]">{error}</p>}
            <button data-testid="register-submit" disabled={loading} className="min-h-[48px] w-full rounded-full font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60" style={{ backgroundColor: "var(--tenant-primary)" }}>
              {loading ? "Creating..." : "Create account & start"}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-neutral-600">Already have an account? <Link data-testid="to-login" to="/login" className="font-semibold tenant-text">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
